import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  Icon,
  Textarea
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'

import IconContainer from 'components/IconContainer/IconContainer'

import { toastProps } from 'utils/types/toast-props'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import {
  ArchiveReasonFormData,
  archiveReasonSchema
} from './ArchiveReasonDialog.schema'

interface ArchiveReasonDialogProps {
  isOpen: boolean
  onClose: () => void
  customerUuid: string
  customerName: string
  customerCategory?: string
  onSuccess?: () => void
}

const ArchiveReasonDialog: React.FC<ArchiveReasonDialogProps> = ({
  isOpen,
  onClose,
  customerUuid,
  customerName,
  customerCategory,
  onSuccess
}) => {
  const [loading, setLoading] = useState<boolean>(false)

  // Define o motivo padrão baseado na categoria do cliente
  const getDefaultReasonType = () => {
    if (
      customerCategory === 'active_customer' ||
      customerCategory === 'inactive_customer'
    ) {
      return 'churn'
    }
    return 'other'
  }

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ArchiveReasonFormData>({
    defaultValues: {
      reasonType: getDefaultReasonType(),
      notes: ''
    },
    resolver: yupResolver(archiveReasonSchema)
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async (data: ArchiveReasonFormData) => {
    try {
      setLoading(true)
      const id = toast.loading('Arquivando cliente...')

      const response = await DataService({
        type: 'POST',
        data: {
          reason_type: data.reasonType,
          notes: data.notes
        },
        url: `/v3/customers/${customerUuid}/archive`
      })

      const errorMessage =
        response.error && (response.detail_error || response.message)

      toast.update(id, {
        render: response.error ? errorMessage : response.data.message,
        type: response.error ? 'error' : 'success',
        ...toastProps
      })

      if (!response.error) {
        handleClose()
        onSuccess?.()
      }
    } catch (error) {
      toast.error('Erro ao arquivar o cliente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      PaperProps={{
        sx: {
          maxWidth: '510px'
        }
      }}
      title=""
      setIsOpenDialog={handleClose}
      isOpenDialog={isOpen}
    >
      <form onSubmit={handleSubmit(onSubmit, toastFirstError)}>
        <Stack alignItems="center" gap={2} mt="-45px">
          <IconContainer icon="archive" />
          <Box>
            <Typography variant="smBold">
              Confirma o arquivamento de {customerName}?
            </Typography>
          </Box>

          <Stack width="100%">
            <Accordion defaultExpanded>
              <AccordionSummary>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="smBold">
                    Motivo do arquivamento
                  </Typography>
                  <Tooltip
                    placement="top"
                    arrow
                    title="É uma informação interna"
                  >
                    <Box sx={{ mt: 0.4 }}>
                      <Icon
                        icon="info"
                        width="18px"
                        height="18px"
                        colorSX={theme.palette.neutral[700]}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Stack gap={2}>
                  <Box>
                    <Controller
                      name="reasonType"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup {...field}>
                          <FormControlLabel
                            value="churn"
                            control={<Radio />}
                            label={
                              <Typography>
                                Churn{' '}
                                <Typography color={theme.palette.neutral[400]}>
                                  (cliente cancelou o serviço)
                                </Typography>
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value="other"
                            control={<Radio />}
                            label={
                              <Stack direction="row" gap={0.2}>
                                <Typography>Outros motivos</Typography>
                                <Typography color={theme.palette.neutral[400]}>
                                  (especificar abaixo)
                                </Typography>
                              </Stack>
                            }
                          />
                        </RadioGroup>
                      )}
                    />
                    {errors.reasonType && (
                      <Typography color="error" variant="smLight">
                        {errors.reasonType.message}
                      </Typography>
                    )}
                  </Box>

                  <Stack gap={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <Typography>Observações</Typography>
                        <Tooltip
                          placement="top"
                          arrow
                          title="Essa informação ficará visível nos detalhes do cliente"
                        >
                          <Box sx={{ mt: 0.4 }}>
                            <Icon
                              icon="info"
                              width="18px"
                              height="18px"
                              colorSX={theme.palette.neutral[700]}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Stack>

                    <Controller
                      name="notes"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Textarea
                          fullWidth
                          style={{
                            height: '80px',
                            resize: 'vertical',
                            maxHeight: '50vh'
                          }}
                          placeholder="Descreva o motivo do arquivamento e outras anotações relevantes"
                          error={!!error}
                          helperText={error?.message}
                          {...field}
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>

          <Stack
            direction="row"
            mt={2}
            gap={1}
            width="100%"
            justifyContent="end"
          >
            <Button
              variantButton="neutral"
              onClick={handleClose}
              disabled={loading}
              type="button"
              fullWidth
            >
              Voltar
            </Button>
            <Button
              fullWidth
              variantButton="primary"
              type="submit"
              disabled={loading}
              loading={loading}
            >
              Arquivar
            </Button>
          </Stack>
        </Stack>
      </form>
    </Dialog>
  )
}

export default ArchiveReasonDialog
